import React__default from 'react';
import { toast } from 'react-toastify';
import { Box } from '../Box/index.js';
import { Flex } from '../Flex/index.js';
import { Icon } from '../Icon/index.js';
import { CloseButton } from '../CloseButton/index.js';
import { Button } from '../Button/index.js';
import { Text } from '../Text/index.js';
export { ToastContainer } from './ToastContainer.js';

const ToastItem = _ref => {
  let {
    closeToast,
    dataTestId,
    title,
    icon,
    iconColor = 'white',
    description,
    onUndo,
    isClosable,
    id,
    toastProps
  } = _ref;
  let toastId = `t${toastProps?.toastId}`;
  if (id) {
    toastId = id;
  }
  return /*#__PURE__*/React__default.createElement(Flex, {
    alignItems: title ? 'flex-start' : 'align-center',
    bg: "carbon800",
    borderRadius: "radiusDefault",
    boxShadow: "medium",
    "data-test-id": dataTestId,
    id: toastId,
    m: "space30",
    maxWidth: 464,
    p: "space50"
  }, icon && /*#__PURE__*/React__default.createElement(Box, null, /*#__PURE__*/React__default.createElement(Icon, {
    color: iconColor,
    dataTestId: "toast-icon",
    mr: "space50",
    name: icon,
    size: "large"
  })), /*#__PURE__*/React__default.createElement(Flex, {
    flexDirection: "column",
    mr: onUndo ? 'space0' : 'space60',
    width: "100%"
  }, title && /*#__PURE__*/React__default.createElement(Text, {
    color: "white",
    fontWeight: "semi",
    size: "fontSize10"
  }, title), /*#__PURE__*/React__default.createElement(Flex, null, /*#__PURE__*/React__default.createElement(Box, {
    mr: "auto"
  }, /*#__PURE__*/React__default.createElement(Text, {
    color: "white",
    display: "inline",
    mr: onUndo ? 'space60' : 'space0'
  }, description), onUndo && /*#__PURE__*/React__default.createElement(Button, {
    color: "blue300",
    display: "inline",
    height: "auto",
    p: "space0",
    size: "medium",
    variant: "none",
    onClick: onUndo
  }, "Undo")))), isClosable && /*#__PURE__*/React__default.createElement(CloseButton, {
    dark: true,
    ml: onUndo ? 'space60' : 'space0',
    onClick: closeToast
  }));
};
/**
 * A Toast is used to notify users about a particular event.
 * We use [react-toastify](https://github.com/fkhadra/react-toastify) under the hood.
 *
 * ### Documentation
 * - [Examples](https://blockparty.endpoint.com/components/toast#usage)
 * - [Props](https://blockparty.endpoint.com/components/toast#props)
 * <br />
 *
 */
function useToast() {
  function notify(_ref2) {
    let {
      description,
      duration = 5000,
      icon,
      iconColor,
      isClosable = true,
      position = 'bottom-left',
      render,
      title,
      onUndo,
      onClose,
      id,
      dataTestId = 'toast',
      role = 'status'
    } = _ref2;
    let containerId = 'ToastContainerBottom';
    if (position === 'top-left' || position === 'top-center' || position === 'top-right') {
      containerId = 'ToastContainerTop';
    }
    const options = {
      onClose,
      autoClose: duration,
      position,
      containerId,
      role
    };
    if (render) {
      return toast(
      // closeToast: onClose is an alias, not the same as above onClose
      _ref3 => {
        let {
          closeToast: onClose,
          toastProps
        } = _ref3;
        const id = `${toastProps?.toastId}`;
        return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, render({
          onClose,
          id
        }));
      }, options);
    }
    // `closeToasts` and `toastProps` are injected with `options`
    toast( /*#__PURE__*/React__default.createElement(ToastItem, {
      dataTestId: dataTestId,
      description: description,
      icon: icon,
      iconColor: iconColor,
      id: id,
      isClosable: isClosable,
      role: role,
      title: title,
      onUndo: onUndo
    }), options);
  }
  return notify;
}

export { useToast };
